/* File: echartsHydrator.js
 * Hydrates a server-built ECharts shell (no rows) with DuckDB rows on the client.
 * - shell: the VisualEChartsShellDTO JSON (PascalCase props)
 * - rows:  array of row objects from DuckDB (column names as returned)
 * - canonicalToSource (optional): map from canonical => actual column names when they differ
 *                                 e.g. { x: "OrderDate", y: "Amount", SeriesName: "CategoryName", name: "Customer", value: "total_sales" }
 */

export function hydrateOption(shell, rows, canonicalToSource) {
  const option = JSON.parse(shell.OptionJson || "{}");
  rows = Array.isArray(rows) ? rows : [];

  const seriesType = (shell.SeriesType || option?.series?.[0]?.type || "bar").trim();

  // ---------- DATASET + ENCODE PATH ----------
  if (shell.UsesDatasetEncode) {
    ensureDataset(option);

    // Only replace server-provided dataset when we actually have client rows
    const hasIncoming = rows.length > 0;
    if (hasIncoming) {
      option.dataset[0].source = rows;
    } else if (!Array.isArray(option.dataset?.[0]?.source)) {
      option.dataset[0].source = [];
    }

    // Keep a valid series object and ensure type is correct
    ensureSeries(option);
    option.series[0].type = seriesType;

    // Remove cartesian scaffolding for non-XY charts (prevents "yAxis '0' not found")
    if (!isXY(seriesType)) {
      delete option.xAxis;
      delete option.yAxis;
      delete option.grid;
    }

    // Adjust ENCODEs based on type (only pie needs special handling here)
    // Use incoming rows if present, otherwise inspect server's sample data
    const sample = hasIncoming
      ? option.dataset[0].source
      : (Array.isArray(option.dataset?.[0]?.source) ? option.dataset[0].source : []);

    if (seriesType === "pie") {
      const enc = (option.series[0].encode = option.series[0].encode || {});
      const nameCol = (canonicalToSource && canonicalToSource.name) || "name";
      enc.itemName = nameCol;

      // Prefer canonical mapping; else find a numeric column (excluding the name column); else fallback "value"
      let valueCol = canonicalToSource && canonicalToSource.value;
      if (!valueCol) {
        valueCol = pickNumericColumn(sample, [nameCol]) || "value";
      }
      enc.value = valueCol;
    }

    return option;
  }

  // ---------- NON-DATASET PATH (SHAPERS) ----------
  const kind = (shell.Plan && shell.Plan.Kind) || guessKind(seriesType);
  const roles = (shell.Plan && shell.Plan.Roles) || shell.CanonicalBindings || {};
  const roleToSource = resolveRoleSources(roles, canonicalToSource);

  switch (kind) {
    case "xy":          return hydrateXY(option, rows, roleToSource);
    case "pie":         return hydratePie(option, rows, roleToSource);
    case "scatter":     return hydrateScatter(option, rows, roleToSource);
    case "heatmap":     return hydrateHeatmap(option, rows, roleToSource);
    case "map":         return hydrateMap(option, rows, roleToSource);
    case "graph":       return hydrateGraph(option, rows, roleToSource);
    case "tree":        return hydrateTree(option, rows, roleToSource);
    case "candlestick": return hydrateCandle(option, rows, roleToSource);
    case "boxplot":     return hydrateBoxplot(option, rows, roleToSource);
    case "radar":       return hydrateRadar(option, rows, roleToSource);
    case "parallel":    return hydrateParallel(option, rows, roleToSource);
    case "funnel":      return hydrateFunnel(option, rows, roleToSource);
    case "gauge":       return hydrateGauge(option, rows, roleToSource);
    case "lines":       return hydrateLines(option, rows, roleToSource);
    case "themeRiver":  return hydrateThemeRiver(option, rows, roleToSource);
    default:            return option;
  }
}

/* ----------------------------
 * Role → source resolver
 * ---------------------------- */
function resolveRoleSources(roles, canonicalToSource) {
  // roles keys: role names (x,y,name,value,SeriesName,...)
  // roles values: canonical names ("x","y","SeriesName",...)
  // If canonicalToSource is provided, map canonical -> actual column
  const out = {};
  for (const role in roles) {
    const canonical = roles[role];
    if (canonicalToSource && canonicalToSource[canonical]) {
      out[role] = canonicalToSource[canonical];
    } else {
      // Fall back: assume DuckDB columns already use canonical names
      out[role] = canonical;
    }
  }
  return out;
}

/* ----------------------------
 * Dataset helpers
 * ---------------------------- */
function ensureDataset(option) {
  option.dataset = option.dataset || [];
  if (!option.dataset.length) option.dataset.push({ id: "main", source: [] });
  if (!("source" in option.dataset[0])) option.dataset[0].source = [];
}
function ensureSeries(option) {
  option.series = option.series || [];
  if (!option.series.length) option.series.push({ type: "bar", data: [] });
}

/* ----------------------------
 * Utilities
 * ---------------------------- */
function isXY(t) {
  return ["line","bar","pictorialBar","scatter","effectScatter","heatmap","candlestick"].includes((t || "").toLowerCase());
}
function toStr(v) {
  if (v == null) return null;
  if (v instanceof Date) return v.toISOString();
  const s = String(v);
  return s.length ? s : null;
}
function toNum(v) {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}
function guessKind(seriesType) {
  switch ((seriesType || "").toLowerCase()) {
    case "line":
    case "bar":
    case "pictorialbar": return "xy";
    default: return seriesType || "xy";
  }
}
function pickNumericColumn(rows, exclude = []) {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  const keys = Object.keys(rows[0] || {});
  for (const k of keys) {
    if (exclude.includes(k)) continue;
    const hasNumber = rows.some(r => Number.isFinite(Number(r?.[k])));
    if (hasNumber) return k;
  }
  return null;
}

/* ----------------------------
 * XY (line/bar/pictorialBar)
 * ---------------------------- */
function hydrateXY(option, rows, roles) {
  const rx = roles.x || "x";
  const ry = roles.y || "y";
  const rs = roles.SeriesName || "SeriesName";

  const categories = [];
  const groups = new Map(); // name -> Map<x, y?>
  for (const r of rows) {
    const x = toStr(r[rx]); if (x == null) continue;
    if (!categories.includes(x)) categories.push(x);
    const y = toNum(r[ry]);
    const name = toStr(r[rs]) || "Series 1";
    if (!groups.has(name)) groups.set(name, new Map());
    groups.get(name).set(x, y);
  }

  const series = [];
  for (const [name, dict] of groups) {
    const data = categories.map(c => (dict.has(c) ? dict.get(c) : null));
    series.push({ name, data });
  }

  ensureObj(option, "xAxis");
  option.xAxis.data = categories;
  ensureSeries(option);

  if (option.series.length >= series.length) {
    for (let i = 0; i < series.length; i++) {
      option.series[i].name = series[i].name;
      option.series[i].data = series[i].data;
    }
  } else {
    const base = option.series[0] || { type: "bar" };
    option.series = series.map(s => ({ ...base, name: s.name, data: s.data }));
  }

  return option;
}

/* ----------------------------
 * Pie / Funnel
 * ---------------------------- */
function hydratePie(option, rows, roles) {
  const kName = roles.name || "name";
  const kValue = roles.value || "value";

  const data = rows
    .map(r => ({ name: toStr(r[kName]), value: toNum(r[kValue]) || 0 }))
    .filter(x => x.name != null);

  ensureSeries(option);
  // Ensure series type is pie
  option.series[0].type = "pie";
  option.series[0].data = data;
  // Remove cartesian bits if present
  delete option.xAxis;
  delete option.yAxis;
  delete option.grid;
  return option;
}

function hydrateFunnel(option, rows, roles) {
  // reuse pie logic with stage->name
  const remapped = { name: roles.stage || "stage", value: roles.value || "value" };
  return hydratePie(option, rows, remapped);
}

/* ----------------------------
 * Scatter / EffectScatter
 * ---------------------------- */
function hydrateScatter(option, rows, roles) {
  const rx = roles.x || "x", ry = roles.y || "y";
  const rs = roles.SeriesName || "SeriesName";
  const rsize = roles.size || "size";

  const buckets = new Map(); // name -> number[][]
  for (const r of rows) {
    const x = toNum(r[rx]), y = toNum(r[ry]);
    if (x == null || y == null) continue;
    const name = toStr(r[rs]) || "Series 1";
    const size = toNum(r[rsize]);
    const arr = buckets.get(name) || [];
    const pt = [x, y];
    if (size != null) pt.push(size);
    arr.push(pt);
    buckets.set(name, arr);
  }

  ensureSeries(option);
  const base = option.series[0] || { type: "scatter" };
  option.series = Array.from(buckets.entries()).map(([name, data]) => ({ ...base, name, data }));
  return option;
}

/* ----------------------------
 * Heatmap
 * ---------------------------- */
function hydrateHeatmap(option, rows, roles) {
  const rx = roles.x || "x", ry = roles.y || "y", rv = roles.value || "value";
  const data = rows
    .map(r => ({ x: toStr(r[rx]), y: toStr(r[ry]), value: toNum(r[rv]) }))
    .filter(d => d.x != null && d.y != null && d.value != null);
  ensureSeries(option);
  option.series[0].data = data;
  return option;
}

/* ----------------------------
 * Map (choropleth)
 * ---------------------------- */
function hydrateMap(option, rows, roles) {
  const idK = roles.id || "id", vK = roles.value || "value";
  const data = rows
    .map(r => ({ id: toStr(r[idK]), value: toNum(r[vK]) }))
    .filter(d => d.id != null && d.value != null);
  ensureSeries(option);
  option.series[0].data = data;
  return option;
}

/* ----------------------------
 * Graph / Sankey
 * ---------------------------- */
function hydrateGraph(option, rows, roles) {
  const sK = roles.source || "source";
  const tK = roles.target || "target";
  const vK = roles.value || "value";
  const cK = roles.category || "category"; // optional

  const links = [];
  const nodeSet = new Set();
  const nodeCat = new Map();

  for (const r of rows) {
    const s = toStr(r[sK]);
    const t = toStr(r[tK]);
    if (!s || !t) continue;
    const v = toNum(r[vK]) || 0;
    links.push({ source: s, target: t, value: v });
    nodeSet.add(s); nodeSet.add(t);
    const cS = toStr(r[cK]);
    if (cS) nodeCat.set(s, cS);
    const cT = toStr(r[cK + "_target"]);
    if (cT) nodeCat.set(t, cT);
  }

  const nodes = Array.from(nodeSet).map(id => {
    const obj = { name: id };
    const cat = nodeCat.get(id);
    if (cat) obj.category = cat;
    return obj;
  });

  ensureSeries(option);
  option.series[0].data = nodes;
  option.series[0].links = links;
  return option;
}

/* ----------------------------
 * Tree / Treemap / Sunburst
 * ---------------------------- */
function hydrateTree(option, rows, roles) {
  const idK = roles.id || "id", nameK = roles.name || "name";
  const parentK = roles.parent || "parent", valueK = roles.value || "value";

  const nodes = new Map(); // id -> node
  const getNode = (id) => {
    if (!nodes.has(id)) nodes.set(id, { name: "", children: [] });
    return nodes.get(id);
  };

  let root = null;
  for (const r of rows) {
    const id = toStr(r[idK]); const name = toStr(r[nameK]);
    if (!id || !name) continue;
    const parent = toStr(r[parentK]);
    const val = toNum(r[valueK]);

    const n = getNode(id);
    n.name = name;
    if (val != null) n.value = val;

    if (parent && parent !== "") {
      const p = getNode(parent);
      p.children = p.children || [];
      if (!p.children.includes(n)) p.children.push(n);
    } else {
      root = n;
    }
  }

  if (!root) root = { name: "Root", children: [] };

  ensureSeries(option);
  option.series[0].data = [root];
  return option;
}

/* ----------------------------
 * Candlestick
 * ---------------------------- */
function hydrateCandle(option, rows, roles) {
  const timeK = roles.time || "time";
  const oK = roles.open || "open", cK = roles.close || "close";
  const lK = roles.low || "low", hK = roles.high || "high";

  const sorted = rows.slice().sort((a, b) => {
    const ta = toStr(a[timeK]) || "", tb = toStr(b[timeK]) || "";
    return ta.localeCompare(tb);
  });

  const categories = [];
  const data = [];
  for (const r of sorted) {
    const t = toStr(r[timeK]); if (!t) continue;
    const o = toNum(r[oK]), c = toNum(r[cK]), l = toNum(r[lK]), h = toNum(r[hK]);
    if ([o,c,l,h].some(v => v == null)) continue;
    categories.push(t);
    data.push([o, c, l, h]);
  }

  ensureObj(option, "xAxis");
  option.xAxis.data = categories;
  ensureSeries(option);
  option.series[0].data = data;
  return option;
}

/* ----------------------------
 * Boxplot (simple)
 * ---------------------------- */
function hydrateBoxplot(option, rows, roles) {
  const k = roles.values || "values";
  const data = [];
  for (const r of rows) {
    const arr = r[k];
    if (!Array.isArray(arr)) continue;
    const nums = arr.map(toNum).filter(v => v != null);
    data.push(nums);
  }
  ensureSeries(option);
  option.series[0].data = data;
  return option;
}

/* ----------------------------
 * Radar
 * ---------------------------- */
function hydrateRadar(option, rows, roles) {
  const sK = roles.SeriesName || "SeriesName";
  const aK = roles.axis || "axis";
  const vK = roles.value || "value";

  const axes = Array.from(new Set(rows.map(r => toStr(r[aK])).filter(Boolean)));
  const maxByAxis = new Map(axes.map(a => [a, 0]));
  for (const r of rows) {
    const a = toStr(r[aK]); const v = toNum(r[vK]);
    if (a && v != null) maxByAxis.set(a, Math.max(maxByAxis.get(a), v));
  }

  const indicators = axes.map(a => ({ name: a, max: Math.max(1, (maxByAxis.get(a) || 0) * 1.2) }));

  const bySeries = new Map();
  for (const r of rows) {
    const s = toStr(r[sK]) || "Series 1";
    if (!bySeries.has(s)) bySeries.set(s, []);
  }
  for (const [s, _list] of bySeries) {
    const values = axes.map(a => {
      const rec = rows.find(r => toStr(r[aK]) === a && (toStr(r[sK]) || "Series 1") === s);
      return rec ? toNum(rec[vK]) || 0 : 0;
    });
    bySeries.set(s, values);
  }

  ensureObj(option, "radar");
  option.radar.indicator = indicators;

  ensureSeries(option);
  const base = option.series[0] || { type: "radar" };
  option.series = Array.from(bySeries.entries()).map(([name, values]) => ({
    ...base,
    name,
    data: [{ name, value: values }]
  }));
  return option;
}

/* ----------------------------
 * Parallel
 * ---------------------------- */
function hydrateParallel(option, rows, roles) {
  // Build dimensions from roles (excluding SeriesName) or infer from first row
  const dims = Object.values(roles).filter(v => v !== "SeriesName");
  const dimensions = dims.length ? dims : (rows[0] ? Object.keys(rows[0]) : []);
  const data = rows.map(r => dimensions.map(d => {
    const v = r[d];
    const n = toNum(v);
    return n != null ? n : (v == null ? "" : String(v));
  }));

  ensureSeries(option);
  option.series[0].data = data;
  ensureObj(option, "parallelAxis");
  return option;
}

/* ----------------------------
 * Gauge
 * ---------------------------- */
function hydrateGauge(option, rows, roles) {
  const nK = roles.name || "name", vK = roles.value || "value";
  const r = rows[0] || {};
  const name = toStr(r[nK]) || "Value";
  const value = toNum(r[vK]) || 0;
  ensureSeries(option);
  option.series[0].data = [{ name, value }];
  // No cartesian axes for gauge
  delete option.xAxis;
  delete option.yAxis;
  delete option.grid;
  return option;
}

/* ----------------------------
 * Lines (geo)
 * ---------------------------- */
function hydrateLines(option, rows, roles) {
  const flon = roles.fromLng || "fromLng", flat = roles.fromLat || "fromLat";
  const tlon = roles.toLng || "toLng", tlat = roles.toLat || "toLat";
  const data = [];
  for (const r of rows) {
    const a = toNum(r[flon]), b = toNum(r[flat]), c = toNum(r[tlon]), d = toNum(r[tlat]);
    if ([a,b,c,d].some(x => x == null)) continue;
    data.push({ coords: [[a, b], [c, d]] });
  }
  ensureSeries(option);
  option.series[0].data = data;
  // Not cartesian
  delete option.xAxis;
  delete option.yAxis;
  delete option.grid;
  return option;
}

/* ----------------------------
 * ThemeRiver
 * ---------------------------- */
function hydrateThemeRiver(option, rows, roles) {
  const dK = roles.date || "date", vK = roles.value || "value", nK = roles.name || "name";
  const data = rows
    .map(r => ({ date: toStr(r[dK]), value: toNum(r[vK]), name: toStr(r[nK]) }))
    .filter(x => x.date && x.value != null && x.name);
  ensureSeries(option);
  option.series[0].data = data;
  // Not cartesian
  delete option.xAxis;
  delete option.yAxis;
  delete option.grid;
  return option;
}

function ensureObj(root, path) {
  const parts = path.split(".");
  let cur = root;
  for (const p of parts) cur = (cur[p] = cur[p] || {});
  return cur;
}
